/** LeadsStaff group (canonical `a3FSi1Fj`). Bearer JWT, staff-gated — role admin
 *  OR staff; anything else gets accessdenied "Staff access required" (403).
 *  Lead pipeline: list/detail/edit/stage/consultation/create-quote. */

import { api } from "./client";
import { normaliseLineItems } from "@/lib/lineItems";
import type {
  Lead,
  LeadCreateInput,
  LeadCreateResponse,
  LeadDetail,
  LeadListResponse,
  LeadListFilters,
  LeadPatch,
  LeadStage,
  ConsultationOutcome,
  FeasibilitySummary,
  Quote,
  ReportEngagement,
} from "@/types";

/** POST /lead (api 214) — manually add a direct-contact lead. Always inserts
 *  (duplicates are intentional); the server maps `lead_source` → source/medium.
 *  Returns the new lead row (unwrapped from the `{ ok, lead }` envelope). */
export async function createLead(input: LeadCreateInput): Promise<Lead> {
  const res = await api.post<LeadCreateResponse>("leads", "lead", { body: input });
  return res.lead;
}

/** GET /lead — pipeline list (standard Xano paged envelope). */
export function listLeads(filters: LeadListFilters = {}): Promise<LeadListResponse> {
  return api.get<LeadListResponse>("leads", "lead", {
    query: {
      stage: filters.stage,
      q: filters.q,
      include_archived: filters.include_archived,
      page: filters.page,
      per_page: filters.per_page,
    },
  });
}

/** GET /lead/{id} — full lead + linked quote (or null). */
export function getLead(leadId: number): Promise<LeadDetail> {
  return api.get<LeadDetail>("leads", `lead/${leadId}`);
}

/** PATCH /lead/{id} — present-key patch envelope. Only keys in `patch` are
 *  written; send "" / 0 to clear, omit to leave untouched. */
export function patchLead(leadId: number, patch: LeadPatch): Promise<Lead> {
  return api.patch<Lead>("leads", `lead/${leadId}`, { body: { patch } });
}

/** POST /lead/{id}/stage — move stage. `lost` stamps lost_at; other stages
 *  clear it. `disqualified` records the reason. */
export function moveStage(
  leadId: number,
  stage: LeadStage,
  disqualifiedReason = "",
): Promise<Lead> {
  return api.post<Lead>("leads", `lead/${leadId}/stage`, {
    body: { stage, disqualified_reason: disqualifiedReason },
  });
}

/** POST /lead/{id}/spam (api 344) — mark / unmark a lead as spam. Spam is a
 *  flavour of disqualified, not a stage: marking stamps `spam_at` and
 *  auto-moves the lead to disqualified (writing reason "Spam" if none);
 *  unmarking clears `spam_at` but leaves the lead disqualified. The server
 *  also pushes spam=true/false to WhatConverts (undocumented but verified WC
 *  edit-lead param) so spam drops out of WC reporting, and api 219 excludes
 *  spam leads from every dashboard lead KPI. */
export function setLeadSpam(leadId: number, spam: boolean): Promise<Lead> {
  return api.post<Lead>("leads", `lead/${leadId}/spam`, { body: { spam } });
}

/** POST /lead/{id}/consultation — record outcome (+ optional notes). On `held`
 *  advances stage to consulted unless already further along. */
export function recordConsultation(
  leadId: number,
  outcome: ConsultationOutcome,
  notes?: string,
): Promise<Lead> {
  const body: { consultation_outcome: ConsultationOutcome; consultation_notes?: string } = {
    consultation_outcome: outcome,
  };
  if (notes && notes.trim()) body.consultation_notes = notes.trim();
  return api.post<Lead>("leads", `lead/${leadId}/consultation`, { body });
}

/** POST /lead/{id}/create-quote — seed a draft quote from the lead + template,
 *  link it, set stage=quoted. 400 if already linked. Returns quote + line_items.
 *
 *  `shortDescription` becomes the quote's `project_description` — the value behind
 *  the `{{short_description}}` token that completes the "this quote covers…" sentence.
 *  It is typed by staff in the drawer, NOT copied from the lead's project description:
 *  the enquiry text reads as the client wrote it, the quote line needs a noun phrase.
 *  The endpoint (api 211) trims it and rejects an empty value. */
export function createQuoteFromLead(
  leadId: number,
  templateId: number,
  shortDescription: string,
): Promise<Quote> {
  return api
    .post<Quote>("leads", `lead/${leadId}/create-quote`, {
      body: { template_id: templateId, short_description: shortDescription.trim() },
    })
    .then(normaliseLineItems);
}

/** POST /lead/{id}/feasibility-report/begin — mint (or reuse) the stable report token and bump
 *  the version, immediately before /generate. Also writes the WEB url to
 *  `lead.feasibility_report_url`, so the drawer's link points at the report page.
 *
 *  The token is stable across regeneration — a link already sent keeps working; only the R2 keys
 *  are version-scoped. Call this FIRST: the engine needs the token to publish under. */
export function beginFeasibilityReport(leadId: number): Promise<{
  token: string;
  web_url: string;
  report_id: number;
  version: number;
}> {
  return api.post("leads", `lead/${leadId}/feasibility-report/begin`, { body: {} });
}

/** POST /lead/{id}/feasibility-report/sent — stamp the link as sent to the client (api 328).
 *
 *  A staff action because nothing here sends the email: the link is copied out of the drawer and
 *  pasted into whatever the studio actually uses, so the human saying "sent" is the only honest
 *  signal. Without it, `sent_at` stays 0 and "sent, not yet opened" — the state the whole
 *  engagement panel exists to report — can never be true.
 *
 *  Re-marking overwrites, and a regenerate does NOT clear it: the token is stable, so a link
 *  already sent keeps working and stays sent. */
export function markReportSent(leadId: number): Promise<{ sent_at: number }> {
  return api.post("leads", `lead/${leadId}/feasibility-report/sent`, { body: {} });
}

/** GET /lead/{id}/report-engagement — how the client actually read the report (api 326).
 *
 *  Staff previews and automated fetches are excluded server-side, from the session list and the
 *  dwell join as well as the counters, so everything this returns is real readers. `report` is
 *  null when the lead has no report row. */
export function getReportEngagement(leadId: number): Promise<ReportEngagement> {
  return api.get<ReportEngagement>("leads", `lead/${leadId}/report-engagement`);
}

/** POST /lead/{id}/feasibility-report — record the summary against the lead.
 *
 *  No longer multipart and no longer sends the PDF: R2 holds it and the Worker serves it behind
 *  the token. This writes `feasibility_summary` (the drawer's at-a-glance panel) and routes
 *  `constraints` onto the lead. It no longer touches `feasibility_report_url` — `begin` owns that
 *  — and no longer requires a quote, so the no-quote-yet `general` variant works. */
export function storeFeasibilityReport(
  leadId: number,
  summary: FeasibilitySummary,
): Promise<Lead> {
  return api.post<Lead>("leads", `lead/${leadId}/feasibility-report`, {
    body: { summary: JSON.stringify(summary) },
  });
}
